<template>
  <div class="wiki-text-container">
    <template v-if="locked && !lockedFull && (!markdownOnly || plaintext)">
      <span v-html="lockedPreviewSnippet"/><span class="locked">{{ lockedBlurredSnippet }}</span>
    </template>
    <template v-if="locked && lockedFull && !markdownOnly">
      <p v-html="plaintextContent" class="locked"/>
    </template>
    <template v-if="locked && markdownOnly && !plaintext">
      <div v-html="markdownContent" class="locked"/>
    </template>

    <template v-if="!locked && markdownOnly">
      <div v-html="markdownContent"/>
    </template>
    <template v-if="!locked && !markdownOnly && plaintext">
      <p v-html="plaintextContent"/>
    </template>
    <template v-if="!locked && !markdownOnly && !plaintext">
      <p>
        <span
          class="wiki-text-token"
          v-for="(token, index) of textTokens"
          :key="index">
          <span v-if="!token.inlineText" v-html="inlineMarkdown(token)"/>
          <a
            v-if="token.type === 'link' && !lockedLinks"
            :href="token.href"
            target="_blank"
            @click.stop>{{ token.inlineText }}</a>
          <tooltip
            :text="'Resources can be accessed with a district or school membership.'"
            v-if="token.type === 'link' && lockedLinks"
          >
            <a
              @click.stop>{{ token.inlineText }}
            </a>
          </tooltip>
          <popover
            v-if="token.type === 'strategy'"
            :placement="popoverPlacement"
            :title="token.title"
            trigger="hover-focus"
            role="tooltip"
            :aria-label="`Open ${token.title} strategy`"
            :transition="50">
            <router-link
              :to="{name: 'StrategyDetailPage', params: {slugOrUuid: token.slug}, query: token.linkQueryParams}"
              target="_blank"
              class="highlight"
              data-role="trigger"
              role="button"
              v-html="inlineMarkdownWithLinkIcon(token.inlineText)"
              @click.stop/>
            <div slot="popover">
              <p>
                <IKImageS3Wrapper
                  class="img-thumbnail"
                  :aws-url="token.image_url"
                  v-if="token.image_url"
                  :alt="token.title"
                  :transformation="[{w: 150}]"
                /></p>
              <p>{{ token.description }}</p>
            </div>
          </popover>
          <popover
            v-if="token.type === 'resource'"
            :placement="popoverPlacement"
            :title="token.title"
            trigger="hover"
            :auto-placement="true">
            <a
              class="highlight highlight--resource"
              data-role="trigger"
              role="button"
              @click.stop><i class="fa fa-download"/> <span v-html="inlineMarkdown(token.inlineText)"/></a>
            <div slot="popover">
              <resource-popover-preview
                :resource="token.resource"
                :description="token.description"
                :image-url="token.image_url"
                :download-links="token.download_links" />
            </div>
          </popover>
        </span>
      </p>
    </template>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'
import { tokenizeWikiText, wikiToPlainText } from '@/util/tokenize_wiki_text'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper'
import ResourcePopoverPreview from '@/components/ResourcePopoverPreview'
import PopoverMixin from '@/mixins/PopoverMixin'
import OpensInNewTabIcon from './OpensInNewTabIcon.vue'

let md = new MarkdownIt({ html: true })

// set up custom render rule to have external links open in a new tag
// Copied from https://github.com/markdown-it/markdown-it/blob/master/docs/architecture.md#renderer
// Remember old renderer, if overriden, or proxy to default renderer
let defaultRender =
  md.renderer.rules.link_open ||
  function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options)
  }

md.renderer.rules.link_open = function(tokens, idx, options, env, self) {
  // If you are sure other plugins can't add `target` - drop check below
  let aIndex = tokens[idx].attrIndex('target')

  if (aIndex < 0) {
    tokens[idx].attrPush(['target', '_blank']) // add new attribute
  } else {
    tokens[idx].attrs[aIndex][1] = '_blank' // replace value of existing attr
  }

  // pass token to default renderer.
  return defaultRender(tokens, idx, options, env, self)
}

export default {
  name: 'WikiTextBlock',
  components: {
    ResourcePopoverPreview,
    IKImageS3Wrapper,
    OpensInNewTabIcon
  },
  mixins: [PopoverMixin],
  props: {
    text: {
      type: String,
      default: ''
    },
    strategies: {
      type: Object,
      default() {
        return {}
      }
    },
    resources: {
      type: Object,
      default() {
        return {}
      }
    },
    plaintext: {
      type: Boolean,
      default: false
    },
    popoverPlacement: {
      type: String,
      default: 'right'
    },
    markdownOnly: {
      type: Boolean,
      default: false
    },
    lockedLinks: {
      type: Boolean,
      default: false
    },
    // if true, we try to show a teaser snippet in plain text and blur the rest
    locked: {
      type: Boolean,
      default: false
    },
    // if true, we blur the entire region
    lockedFull: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    textTokens: function() {
      return tokenizeWikiText(this.text, this.strategies || {}, this.resources || {})
    },
    plaintextContent: function() {
      return this.inlineMarkdown(wikiToPlainText(this.text))
    },
    markdownContent: function() {
      return this.markdown(this.text)
    },
    lockedPreviewSnippet: function() {
      let plaintext = wikiToPlainText(this.text)
      let words = plaintext.split(' ')
      return words.slice(0, words.length / 2).join(' ') + '...'
    },
    lockedBlurredSnippet: function() {
      let plaintext = wikiToPlainText(this.text)
      let words = plaintext.split(' ')
      return words.slice(words.length / 2).join(' ')
    }
  },
  methods: {
    inlineMarkdown(str) {
      return md.renderInline(str)
    },
    markdown(str) {
      return md.render(str)
    },
    openDownloadLink(url) {
      window.open(url)
    },
    inlineMarkdownWithLinkIcon(str) {
      return md.renderInline(str + " <i class='fa fa-external-link' /> <span class='sr-only'>(Opens in a new tab)</span>" )
    }
  }
}
</script>

<style lang="less">
.wiki-text-container {
  img {
    max-width: 100%;
  }

  h1,
  h2,
  h3 {
    line-height: 1.3;
  }
  h4,
  h5,
  h6 {
    line-height: inherit; // should be 1.43 same as default line-height on body element
  }
}
.wiki-text-token {
  display: inline;
}
.highlight {
  display: inline-block;
  background: #ffff84;
  padding: 2px 4px;
  margin: 1px 0;
}
.highlight.highlight--resource {
  background: #ffd8fc;
  cursor: default;
  &:hover {
    text-decoration: none;
  }
}
.img-thumbnail {
  max-height: 120px;
}

.fa.fa-external-link {
  font-size: 75%;
}
</style>
